import { CalendarClock, CircleCheck } from "lucide-react";
import { Card } from "../ui/Card";
import {
  forecastTimelineLayout,
  lastCheckinSummary,
  nextCheckinDate,
  type CheckinView,
} from "../../screens/plan-utils";
import {
  formatGoalDateRange,
  formatShortDate,
  formatWeeklyChange,
  formatWeeksRange,
  type EngineSnapshot,
} from "../progress/utils";

export interface ForecastCardProps {
  readonly snapshot: EngineSnapshot;
  readonly pending: CheckinView | null;
  readonly checkins: readonly CheckinView[];
  readonly planStartedAt: number | undefined;
}

export function ForecastCard({ snapshot, pending, checkins, planStartedAt }: ForecastCardProps) {
  const lastResolved = checkins.find((row) => row.status !== "pending") ?? null;
  const anchor = pending?.createdAt ?? lastResolved?.createdAt ?? null;
  const nextAt = nextCheckinDate(anchor, planStartedAt);
  const layout = forecastTimelineLayout({
    todayMs: Date.now(),
    nextCheckinAtMs: nextAt,
    goalMinMs: snapshot.goalDateMin,
    goalMaxMs: snapshot.goalDateMax,
  });
  const range = formatGoalDateRange(snapshot.goalDateMin, snapshot.goalDateMax);
  const weeks = formatWeeksRange(snapshot.weeksMin, snapshot.weeksMax);

  return (
    <Card className="flex flex-col gap-4 p-4" data-testid="plan-timeline-card">
      <div className="flex items-center gap-2">
        <div className="rounded-full bg-accent-light p-2 text-accent">
          <CalendarClock size={18} strokeWidth={2} />
        </div>
        <h3 className="text-base font-semibold text-text">Forecast</h3>
      </div>

      {layout !== null && range !== null ? (
        <>
          <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
            <p className="text-xl font-bold leading-none text-text" data-testid="timeline-weeks">
              {weeks ?? (snapshot.weeksToGoal !== null ? `About ${snapshot.weeksToGoal} weeks` : "Goal in sight")}
            </p>
            <span
              className="rounded-full bg-accent-light px-2.5 py-1 text-[11px] font-semibold text-accent-dark"
              data-testid="timeline-range"
            >
              {range}
            </span>
          </div>

          <div data-testid="forecast-rail">
            <div className="relative h-2.5 w-full rounded-full bg-muted">
              <div
                className="plan-fill-grow absolute top-0 h-full rounded-full bg-accent/30"
                style={{
                  left: `${layout.bandStartFraction * 100}%`,
                  width: `${layout.bandWidthFraction * 100}%`,
                }}
                data-testid="forecast-band"
                aria-hidden
              />
              <span
                className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-surface bg-text-muted"
                style={{ left: 0 }}
                aria-hidden
              />
              {layout.checkinFraction !== null && (
                <span
                  className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-surface bg-accent shadow-sm"
                  style={{ left: `calc(${layout.checkinFraction * 100}% - 7px)` }}
                  aria-hidden
                />
              )}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] leading-tight text-text-muted">
              <div>
                <p className="font-semibold text-text">Today</p>
                <p>{formatShortDate(Date.now())}</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-text">Next weekly check-in</p>
                <p data-testid="checkin-next-date">
                  {nextAt !== null ? formatShortDate(nextAt) : "After your first full week"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-text">Goal window</p>
                <p>
                  {snapshot.goalDateMin !== null ? formatShortDate(snapshot.goalDateMin) : ""}
                  {" – "}
                  {snapshot.goalDateMax !== null ? formatShortDate(snapshot.goalDateMax) : ""}
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm leading-relaxed text-text-muted" data-testid="timeline-pending">
          Keep logging meals and weigh-ins — your estimated goal date appears once your trend is established.
          {nextAt !== null && (
            <>
              {" "}
              Next check-in:{" "}
              <span className="font-semibold text-text" data-testid="checkin-next-date">
                {formatShortDate(nextAt)}
              </span>
              .
            </>
          )}
        </p>
      )}

      <dl className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-muted/60 px-3 py-2">
          <dt className="text-[10px] font-medium uppercase tracking-wide text-text-muted">Planned pace</dt>
          <dd className="mt-0.5 text-sm font-semibold text-text" data-testid="forecast-planned-rate">
            {formatWeeklyChange(snapshot.weeklyChangeKg)}
          </dd>
        </div>
        <div className="rounded-xl bg-muted/60 px-3 py-2">
          <dt className="text-[10px] font-medium uppercase tracking-wide text-text-muted">Actual pace</dt>
          <dd className="mt-0.5 text-sm font-semibold text-text" data-testid="forecast-actual-rate">
            {snapshot.driftKgPerWeek !== null ? formatWeeklyChange(snapshot.driftKgPerWeek) : "—"}
          </dd>
        </div>
      </dl>
      {snapshot.driftKgPerWeek === null && (
        <p className="text-xs leading-relaxed text-text-muted">
          Actual pace appears after enough check-ins.
        </p>
      )}

      {lastResolved !== null && (
        <div className="flex items-start gap-2" data-testid="checkin-last-summary">
          <CircleCheck size={16} className="mt-0.5 shrink-0 text-accent" />
          <p className="text-xs leading-relaxed text-text-muted">
            Last check-in {formatShortDate(lastResolved.createdAt)}: {lastCheckinSummary(lastResolved)}
          </p>
        </div>
      )}
    </Card>
  );
}
